import { useMemo, useState } from 'react';
import { DeploymentDetails } from '../../../../models/organization/deployment';
import {
  applyEdgeDrag,
  TimelineBlock,
  TimelineEdge,
} from '../../../../utils/deployment-timeline/cascade';

export interface DeploymentTimelineEntry extends TimelineBlock {
  organizationName: string;
  missionsCount: number;
  firstMissionDate: number | null;
  lastMissionDate: number | null;
  periodId: string | null;
  periodIndex: number | null;
}

const toBlocks = (deployments: DeploymentDetails[]): DeploymentTimelineEntry[] => {
  return deployments
    .slice()
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
    .map((deployment) => ({
      id: deployment.id,
      start: new Date(deployment.start).getTime(),
      end: deployment.end ? new Date(deployment.end).getTime() : null,
      organizationName: deployment.organizationName,
      missionsCount: deployment.missionsCount,
      firstMissionDate: deployment.firstMissionDate
        ? new Date(deployment.firstMissionDate).getTime()
        : null,
      lastMissionDate: deployment.lastMissionDate
        ? new Date(deployment.lastMissionDate).getTime()
        : null,
      periodId: deployment.deploymentPeriod?.id ?? null,
      periodIndex: deployment.deploymentPeriod?.index ?? null,
    }));
};

const sameEntries = (
  left: DeploymentTimelineEntry[],
  right: DeploymentTimelineEntry[],
): boolean => {
  if (left.length !== right.length) return false;

  return left.every((entry, index) => {
    const other = right[index];
    return (
      entry.id === other.id &&
      entry.start === other.start &&
      entry.end === other.end &&
      entry.organizationName === other.organizationName &&
      entry.missionsCount === other.missionsCount &&
      entry.firstMissionDate === other.firstMissionDate &&
      entry.lastMissionDate === other.lastMissionDate &&
      entry.periodId === other.periodId &&
      entry.periodIndex === other.periodIndex
    );
  });
};

export const useDeploymentTimelineState = (
  deployments: DeploymentDetails[],
  todayCeiling: number,
) => {
  const initial = useMemo(() => toBlocks(deployments), [deployments]);
  const [blocks, setBlocks] = useState<DeploymentTimelineEntry[]>(initial);
  const [snapshot, setSnapshot] = useState<DeploymentTimelineEntry[]>(initial);
  const [adopted, setAdopted] = useState<DeploymentTimelineEntry[]>(initial);

  const dirty = useMemo(
    () =>
      blocks.some(
        (block, index) =>
          block.start !== snapshot[index]?.start || block.end !== snapshot[index]?.end,
      ),
    [blocks, snapshot],
  );

  // The caller may hand us a freshly built array on every render, so adoption has to
  // compare the serialised rows themselves - a reference check would re-adopt forever.
  if (!sameEntries(adopted, initial) && !dirty) {
    setAdopted(initial);
    setBlocks(initial);
    setSnapshot(initial);
  }

  const dragEdge = (index: number, edge: TimelineEdge, proposedDate: number) => {
    setBlocks((current) => {
      const updated = applyEdgeDrag(current, index, edge, proposedDate, todayCeiling);
      return updated.map((block, i) => ({ ...current[i], ...block }));
    });
  };

  const discard = () => {
    setBlocks(snapshot);
  };

  const markSaved = () => {
    setSnapshot(blocks);
  };

  return { blocks, dirty, dragEdge, discard, markSaved };
};
